import { useState } from "react";
import { Link } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import Icon from "@/components/mnf/Icon";
import { GlassCard, PillTag, Btn, Avatar } from "@/components/mnf/UI";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
  type SisterhoodPost,
  type SisterhoodPostKind,
  type SisterhoodReactionKind,
} from "@/lib/store";
import { detectAllSensitive } from "@/lib/contentSafety";

/**
 * Sisterhood
 *
 * The community feed all tiers can read, react, and (after their listening
 * week) post into. Five post kinds cover the shape of what sisters share:
 * testimony, win, ask for prayer, verse, question. Reactions are single-tap
 * Amen, Praying, and Me too. Comments live on the detail page. The feed is
 * reverse chronological with a soft tier-mate interleave.
 *
 * Sisters in their first week see a warm listening-period banner and the
 * compose button is hidden until day 7. Posts that trip the sensitive
 * keyword filter route to Coach Kiley's queue with an inline note before
 * sending.
 */

const KIND_META: Record<
  SisterhoodPostKind,
  { label: string; tone: "coral" | "purple" | "gold" | "forest" | "fiber"; emoji: string }
> = {
  testimony: { label: "Testimony", tone: "coral",  emoji: "✨" },
  win:       { label: "Win",       tone: "gold",   emoji: "🌾" },
  prayer:    { label: "Prayer",    tone: "purple", emoji: "🙏" },
  verse:     { label: "Verse",     tone: "forest", emoji: "📖" },
  question:  { label: "Question",  tone: "fiber",  emoji: "💬" },
};

const REACTION_META: Record<
  SisterhoodReactionKind,
  { label: string; emoji: string }
> = {
  amen:    { label: "Amen",    emoji: "🙌" },
  praying: { label: "Praying", emoji: "🙏" },
  "me-too":{ label: "Me too",  emoji: "💛" },
};

export default function Sisterhood() {
  const me = useStoreQuery(() => queries.currentUser());
  // No fallback to the seeded demo account: posting and reacting below WRITE with this
  // id. See the early return just below.
  const userId = me?.id ?? null;
  const feed = useStoreQuery(() => (userId ? queries.sisterhoodFeed(userId) : []));
  const inListening = useStoreQuery(() => (userId ? queries.isInListeningPeriod(userId) : true));
  const daysLeft = useStoreQuery(() => (userId ? queries.listeningPeriodDaysLeft(userId) : 0));
  const [composeOpen, setComposeOpen] = useState(false);

  if (!userId) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          Sign in to see the Sisterhood, sister.
        </p>
      </div>
    );
  }

  return (
    <>
      <ScreenHeader
        variant="forest"
        eyebrow="The Sisterhood"
        title="A wall of grace"
        subtitle={
          <>
            "As iron sharpens iron, so one woman sharpens another."
            <span style={{ color: "rgba(229,185,151,0.95)" }}> · Proverbs 27:17, paraphrased</span>
          </>
        }
        trailing={
          <Link
            href="/sister-network"
            className="no-underline flex items-center justify-center hover-elevate"
            style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "rgba(245,240,232,0.12)", color: "#F5F0E8",
              border: "1px solid rgba(245,240,232,0.18)",
            }}
            aria-label="Sister Network"
            data-testid="link-sister-network"
          >
            <Icon name="diversity_3" size={18} />
          </Link>
        }
      />

      <div className="px-5 pt-4 pb-28 space-y-4" style={{ background: "var(--mnf-cream)" }}>
        {inListening && (
          <ListeningBanner daysLeft={daysLeft} />
        )}

        {!inListening && (
          <button
            onClick={() => setComposeOpen(true)}
            data-testid="button-compose-post"
            className="w-full flex items-center gap-3 hover-elevate active-elevate-2"
            style={{
              padding: "14px 16px",
              borderRadius: 18,
              background: "linear-gradient(165deg, rgba(168,212,184,0.25) 0%, rgba(192,120,74,0.12) 100%)",
              border: "1px solid rgba(30,74,53,0.10)",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <span
              className="flex items-center justify-center"
              style={{
                width: 38, height: 38, borderRadius: "50%",
                background: "var(--mnf-f)", color: "#F5F0E8",
              }}
            >
              <Icon name="add" size={20} weight={500} />
            </span>
            <div className="flex-1">
              <p className="mnf-serif italic" style={{ fontSize: 16, color: "var(--mnf-fd)" }}>
                Share with the sisterhood
              </p>
              <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)" }}>
                Testimony, win, prayer, verse, or honest question
              </p>
            </div>
          </button>
        )}

        {feed.length === 0 && (
          <GlassCard padding={20}>
            <p className="mnf-body italic" style={{ fontSize: 13, color: "var(--mnf-tg)", textAlign: "center" }}>
              No posts yet. The feed is quiet, your voice is welcome here.
            </p>
          </GlassCard>
        )}

        {feed.map((post) => (
          <FeedPostCard key={post.id} post={post} viewerId={userId} />
        ))}
      </div>

      {composeOpen && (
        <ComposeSheet authorId={userId} onClose={() => setComposeOpen(false)} />
      )}
    </>
  );
}

/* --------------------------- Listening period --------------------------- */

function ListeningBanner({ daysLeft }: { daysLeft: number }) {
  return (
    <div
      className="flex items-start gap-3 p-4"
      style={{
        background: "rgba(127,119,221,0.10)",
        border: "1px solid rgba(127,119,221,0.30)",
        borderRadius: 18,
      }}
      data-testid="banner-listening-period"
    >
      <span
        className="flex items-center justify-center shrink-0"
        style={{
          width: 36, height: 36, borderRadius: "50%",
          background: "var(--mnf-purpleL)", color: "var(--mnf-purple)",
        }}
      >
        <Icon name="hearing" size={18} weight={500} />
      </span>
      <div className="flex-1">
        <p className="mnf-serif" style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.25 }}>
          Listening week, sister
        </p>
        <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.5, marginTop: 4 }}>
          For your first week, read and react. Posting opens in {daysLeft} day{daysLeft === 1 ? "" : "s"}, so the feed enters your bones first.
        </p>
      </div>
    </div>
  );
}

/* ------------------------------ Post card ------------------------------ */

function FeedPostCard({ post, viewerId }: { post: SisterhoodPost; viewerId: string }) {
  const author = useStoreQuery(() => queries.userById(post.authorId));
  const reactions = useStoreQuery(() => queries.sisterhoodReactionsForPost(post.id));
  const myReaction = useStoreQuery(() => queries.mySisterhoodReactionForPost(viewerId, post.id));
  const commentCount = useStoreQuery(() => queries.sisterhoodCommentsForPost(post.id).length);
  const toggleReaction = useStoreMutation(mutations.togglePostReaction);

  const meta = KIND_META[post.kind];
  const minutes = Math.max(1, Math.floor((Date.now() - post.createdAt) / 60000));
  const ago =
    minutes < 60 ? `${minutes}m ago`
    : minutes < 60 * 24 ? `${Math.floor(minutes / 60)}h ago`
    : `${Math.floor(minutes / (60 * 24))}d ago`;

  return (
    <GlassCard padding={18} data-testid={`feed-post-${post.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <Avatar
          initials={author?.initials ?? "S"}
          src={author?.avatarDataUrl ?? undefined}
          size={42}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="mnf-serif" style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)" }}>
              {author?.name ?? "A sister"}
            </p>
            <PillTag tone={meta.tone}>
              <span style={{ fontSize: 11 }}>{meta.emoji}</span>
              {meta.label}
            </PillTag>
          </div>
          <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 2 }}>
            {ago}
          </p>
        </div>
      </div>

      {post.kind === "verse" && post.verseRef && (
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)", marginBottom: 6 }}>
          {post.verseRef}
        </p>
      )}
      <p
        className={post.kind === "verse" ? "mnf-serif italic" : "mnf-body"}
        style={{
          fontSize: post.kind === "verse" ? 16 : 14,
          color: "var(--mnf-fd)",
          lineHeight: 1.55,
          whiteSpace: "pre-wrap",
        }}
      >
        {post.body}
      </p>

      {post.photoDataUrl && (
        <img
          src={post.photoDataUrl}
          alt=""
          style={{
            width: "100%",
            borderRadius: 14,
            marginTop: 12,
            border: "1px solid rgba(30,74,53,0.08)",
          }}
        />
      )}

      <div className="flex items-center gap-2 mt-4 flex-wrap">
        {(["amen", "praying", "me-too"] as SisterhoodReactionKind[]).map((kind) => {
          const meta = REACTION_META[kind];
          const active = myReaction === kind;
          const count =
            kind === "amen" ? reactions.amenLabel
            : kind === "praying" ? reactions.prayingLabel
            : reactions.meTooLabel;
          const num =
            kind === "amen" ? reactions.amen
            : kind === "praying" ? reactions.praying
            : reactions.meToo;
          return (
            <button
              key={kind}
              onClick={() => toggleReaction({ userId: viewerId, postId: post.id, kind })}
              data-testid={`button-react-${kind}-${post.id}`}
              className="inline-flex items-center gap-1.5 hover-elevate active-elevate-2"
              style={{
                padding: "7px 12px",
                borderRadius: 999,
                background: active ? "var(--mnf-f)" : "rgba(30,74,53,0.06)",
                color: active ? "#F5F0E8" : "var(--mnf-f)",
                border: active ? "1px solid var(--mnf-f)" : "1px solid transparent",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <span style={{ fontSize: 14 }}>{meta.emoji}</span>
              <span>{meta.label}</span>
              {num > 0 && (
                <span style={{ opacity: active ? 0.85 : 0.7, marginLeft: 2 }}>{count}</span>
              )}
            </button>
          );
        })}
        <Link
          href={`/sisterhood/post/${post.id}`}
          className="no-underline ml-auto inline-flex items-center gap-1.5 hover-elevate"
          style={{
            padding: "7px 12px",
            borderRadius: 999,
            background: "transparent",
            color: "var(--mnf-tg)",
            fontSize: 12,
            fontWeight: 600,
          }}
          data-testid={`link-comments-${post.id}`}
        >
          <Icon name="chat_bubble_outline" size={14} />
          {commentCount > 0 ? commentCount : "Comment"}
        </Link>
      </div>
    </GlassCard>
  );
}

/* ----------------------------- Compose sheet ---------------------------- */

const KIND_ORDER: SisterhoodPostKind[] = ["testimony", "win", "prayer", "verse", "question"];

function ComposeSheet({ authorId, onClose }: { authorId: string; onClose: () => void }) {
  const [kind, setKind] = useState<SisterhoodPostKind>("testimony");
  const [body, setBody] = useState("");
  const [verseRef, setVerseRef] = useState("");
  const create = useStoreMutation(mutations.createSisterhoodPost);
  const flagged = detectAllSensitive(body);
  const willQueue = flagged.length > 0;

  const onSubmit = () => {
    if (!body.trim()) return;
    create({
      authorId,
      kind,
      body,
      verseRef: kind === "verse" ? verseRef.trim() || null : null,
    });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 flex items-end justify-center"
      style={{ background: "rgba(22,56,40,0.55)", zIndex: 60 }}
      onClick={onClose}
      data-testid="compose-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          background: "var(--mnf-cream)",
          borderRadius: "24px 24px 0 0",
          padding: 22,
          maxHeight: "88vh",
          overflowY: "auto",
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Share with the sisters</p>
            <h2 className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.15 }}>
              What is on your heart?
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="button-close-compose"
            className="hover-elevate"
            style={{
              width: 36, height: 36, borderRadius: 999,
              background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)",
              border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <p className="mnf-eyebrow mb-2" style={{ color: "var(--mnf-tg)" }}>Choose a kind</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {KIND_ORDER.map((k) => {
            const meta = KIND_META[k];
            const active = kind === k;
            return (
              <button
                key={k}
                onClick={() => setKind(k)}
                data-testid={`chip-kind-${k}`}
                className="inline-flex items-center gap-1.5 hover-elevate active-elevate-2"
                style={{
                  padding: "8px 14px",
                  borderRadius: 999,
                  background: active ? "var(--mnf-f)" : "rgba(30,74,53,0.06)",
                  color: active ? "#F5F0E8" : "var(--mnf-f)",
                  border: active ? "1px solid var(--mnf-f)" : "1px solid transparent",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <span style={{ fontSize: 14 }}>{meta.emoji}</span>
                {meta.label}
              </button>
            );
          })}
        </div>

        {kind === "verse" && (
          <GlassCard padding={14} className="mb-3">
            <label className="mnf-eyebrow block mb-2" style={{ color: "var(--mnf-tg)" }}>Verse reference</label>
            <input
              data-testid="input-verse-ref"
              value={verseRef}
              onChange={(e) => setVerseRef(e.target.value)}
              placeholder="Isaiah 40:31"
              className="w-full mnf-serif italic outline-none bg-transparent"
              style={{
                fontSize: 16, color: "var(--mnf-fd)",
                borderBottom: "1px solid rgba(30,74,53,0.18)", paddingBottom: 6,
              }}
            />
          </GlassCard>
        )}

        <GlassCard padding={14} className="mb-3">
          <label className="mnf-eyebrow block mb-2" style={{ color: "var(--mnf-tg)" }}>
            {kind === "verse" ? "The verse" : kind === "question" ? "Your question" : "Your words"}
          </label>
          <textarea
            data-testid="input-post-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={
              kind === "testimony" ? "What has God been doing in your story?"
              : kind === "win" ? "Celebrate something the Lord did this week."
              : kind === "prayer" ? "Where can your sisters pray with you?"
              : kind === "verse" ? "Share the verse that is meeting you today."
              : "What honest question is the Lord stirring in you?"
            }
            rows={6}
            className="w-full mnf-body outline-none bg-transparent resize-none"
            style={{
              fontSize: 14, color: "var(--mnf-fd)", lineHeight: 1.55,
            }}
          />
        </GlassCard>

        {willQueue && (
          <div
            className="flex items-start gap-3 p-3 mb-3"
            style={{
              background: "rgba(160,120,32,0.10)",
              border: "1px solid rgba(160,120,32,0.30)",
              borderRadius: 14,
            }}
            data-testid="banner-will-queue"
          >
            <span style={{ fontSize: 18 }}>💛</span>
            <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-fd)", lineHeight: 1.5 }}>
              This post will go to Coach Kiley for a quick read before posting. She walks closely on tender topics.
            </p>
          </div>
        )}

        <Btn
          variant="primary"
          block
          size="lg"
          onClick={onSubmit}
          disabled={!body.trim()}
          data-testid="button-submit-post"
        >
          {willQueue ? "Send to Coach Kiley" : "Post to the sisterhood"}
          <Icon name="arrow_forward" size={16} />
        </Btn>
      </div>
    </div>
  );
}
